"use client";
import Ripple from "./ripple";
import Safari from "./safari";
import { cn } from "@/lib/utils";
import { ArrowRight, Zap, Star, Layers, Settings } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const features = [
  {
    title: "Code Snippets Ready for Use",
    description:
      "Instantly integrate copy/paste-ready code into your projects, accelerating development without sacrificing quality.",
    className: "bg-gradient-to-br from-red-500/5 to-transparent",
    icon: <Zap className="h-6 w-6 text-red-500" />,
    color: "red",
    content: (
      <>
        <Safari
          src={`/dummy-image.svg`}
          url="https://fazier.com"
          className="-mb-32 mt-4 max-h-64 w-full px-4 select-none drop-shadow-[0_0_28px_rgba(0,0,0,.1)] translate-y-[-15px]"
        />
      </>
    ),
  },
  {
    title: "Simplified Validation Setup",
    description:
      "Easily implement powerful form validations with support for both Zod and Yup, ensuring data accuracy with minimal effort.",
    className:
      "order-3 xl:order-none bg-gradient-to-br from-blue-500/5 to-transparent",
    icon: <Star className="h-6 w-6 text-blue-500" />,
    color: "blue",
    content: (
      <Safari
        src={`/dummy-image.svg`}
        url="https://fazier.com"
        className="-mb-32 mt-4 max-h-64 w-full px-4 select-none drop-shadow-[0_0_28px_rgba(0,0,0,.1)] translate-y-[-15px]"
      />
    ),
  },
  {
    title: "Comprehensive Components",
    description:
      "Access a complete set of pre-built components tailored for dynamic forms, saving you time and effort.",
    className:
      "md:row-span-2 bg-gradient-to-br from-orange-500/5 to-transparent",
    icon: <Layers className="h-6 w-6 text-orange-500" />,
    color: "orange",
    content: (
      <>
        <Safari
          src={`/dummy-image.svg`}
          url="https://fazier.com"
          className="-mb-48 ml-12 mt-16 h-full px-4 select-none drop-shadow-[0_0_28px_rgba(0,0,0,.1)] translate-x-[-15px]"
        />
      </>
    ),
  },
  {
    title: "Flexible Customization Options",
    description:
      "Customize form behavior effortlessly with support for Yup, server-side rendering, and other powerful tools.",
    className:
      "flex-row order-4 md:col-span-2 md:flex-row xl:order-none bg-gradient-to-br from-green-500/5 to-transparent",
    icon: <Settings className="h-6 w-6 text-green-500" />,
    color: "green",
    content: (
      <>
        <Ripple className="absolute -bottom-full" />
        <Safari
          src={`/dummy-image.svg`}
          url="https://fazier.com"
          className="-mb-32 mt-4 max-h-64 w-full px-4 select-none drop-shadow-[0_0_28px_rgba(0,0,0,.1)] translate-y-[-15px]"
        />
      </>
    ),
  },
];

export default function GridList03() {
  return (
    <section className="pb-20 pt-20 md:pb-32 md:pt-32 container mx-auto">
      <div className="text-center space-y-4 pb-16 mx-auto max-w-4xl">
        <Badge>FEATURES</Badge>
        <h2 className="mx-auto mt-4 text-3xl font-bold sm:text-5xl tracking-tight">
          Powerful Features
        </h2>
        <p className="text-xl text-muted-foreground pt-1">
          Startups & Corporations sharing our vision at Fazier
        </p>
      </div>
      <div className="mx-auto grid max-w-sm grid-cols-1 gap-8 md:max-w-3xl md:grid-cols-2 xl:grid-rows-2 md:grid-rows-3 xl:max-w-7xl xl:auto-rows-fr xl:grid-cols-3">
        {features.map((feature, index) => (
          <div
            key={index}
            className={cn(
              "relative items-start overflow-hidden p-8 rounded-xl bg-card/80 backdrop-blur-sm border border-white/10",
              feature.className
            )}
          >
            <div className="relative z-10">
              <div className="flex justify-start items-center space-x-4 mb-5">
                <div
                  className={`relative flex items-center justify-center w-12 h-12 rounded-xl bg-${feature.color}-500/10`}
                >
                  {feature.icon}
                </div>
                <h3 className={`font-semibold text-lg`}>{feature.title}</h3>
              </div>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                {feature.description}
              </p>
              <div
                className={`inline-flex items-center font-medium group/link`}
              >
                <span className="relative">Learn more</span>
                <div className="ml-2">
                  <ArrowRight className={`h-4 w-4 text-${feature.color}-500`} />
                </div>
              </div>
            </div>
            {feature.content}
          </div>
        ))}
      </div>
    </section>
  );
}
